import React from 'react';
import { Text } from '@heartinternet/hi-ui';
import { Link } from '@heartinternet/hi-ui';
import { Input } from '@heartinternet/hi-ui';
import { Select } from '@heartinternet/hi-ui';
import { Checkbox } from '@heartinternet/hi-ui';
import { FormField } from '@heartinternet/hi-ui';
import { NumericLimitField } from './NumericLimitField';
import type { EmailOption, PackageTypeStep1Values, ResoldTypeProto, WebServerType } from './types';
import './HostingPackageTypeForm.css';

const WE_CONFIGURE_URL = '/manage/manage-reseller-we.cgi';
const EPRE_CONFIGURE_URL = '/manage/manage-reseller-epre.cgi';
const SNAPSHOT_BACKUPS_URL = '/manage/reseller/backups.cgi';

type LimitFieldKey =
  | 'webspace'
  | 'bandwidth'
  | 'subdomains'
  | 'ftpUsers'
  | 'forwarders'
  | 'responders'
  | 'lists'
  | 'mailboxes';

interface LimitFieldConfig {
  field: LimitFieldKey;
  label: string;
  submitName: string;
  protoKey: keyof ResoldTypeProto;
  unitSuffix?: string;
}

const LIMIT_FIELDS: LimitFieldConfig[] = [
  {
    field: 'webspace',
    label: 'Webspace',
    submitName: 'webspace',
    protoKey: 'disc_space',
    unitSuffix: ' MB',
  },
  {
    field: 'bandwidth',
    label: 'Bandwidth',
    submitName: 'bandwidth',
    protoKey: 'bandwidth',
    unitSuffix: ' MB/month',
  },
  { field: 'subdomains', label: 'Subdomains', submitName: 'subdomains', protoKey: 'subdomains' },
  {
    field: 'ftpUsers',
    label: 'Additional FTP accounts',
    submitName: 'ftp_users',
    protoKey: 'ftp_users',
  },
  {
    field: 'forwarders',
    label: 'Email forwarding',
    submitName: 'forwarders',
    protoKey: 'forwarders',
  },
  {
    field: 'responders',
    label: 'Email autoresponders',
    submitName: 'responders',
    protoKey: 'responders',
  },
  { field: 'lists', label: 'Email distribution lists', submitName: 'lists', protoKey: 'lists' },
  { field: 'mailboxes', label: 'Mailboxes', submitName: 'mailboxes', protoKey: 'mailboxes' },
];

const WINDOWS_DB_OPTIONS: Array<number | ''> = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export interface Step1FieldsProps {
  serverType: WebServerType;
  resoldTypeProto: ResoldTypeProto;
  emails: EmailOption[];
  eprEmails: EmailOption[];
  hasSnapshots: boolean;
  snapshotsEnabled: boolean;
  /** Form-field name suffix for the multi-value `features<N>` checkboxes (e.g. `features${id}` or `features0`). */
  featuresFieldName: string;
  values: PackageTypeStep1Values;
  nameError: string | null;
  onChange: (patch: Partial<PackageTypeStep1Values>) => void;
  onToggleFeature: (code: string, on: boolean) => void;
  onClearNameError: () => void;
}

export const Step1Fields: React.FC<Step1FieldsProps> = ({
  serverType,
  resoldTypeProto,
  emails,
  eprEmails,
  hasSnapshots,
  snapshotsEnabled,
  featuresFieldName,
  values,
  nameError,
  onChange,
  onToggleFeature,
  onClearNameError,
}) => {
  return (
    <div className="hi-ui-hosting-package-type-form-step1">
      <FormField
        label="Web hosting package name"
        labelPosition="left"
        required
        error={nameError ?? undefined}
      >
        <Input
          name="name"
          value={values.name}
          onChange={(e) => {
            onChange({ name: e.target.value });
            if (nameError) onClearNameError();
          }}
          fullWidth
        />
      </FormField>

      {LIMIT_FIELDS.map((f) => (
        <NumericLimitField
          key={f.field}
          label={f.label}
          name={f.submitName}
          value={values[f.field]}
          options={resoldTypeProto[f.protoKey]}
          unitSuffix={f.unitSuffix}
          onChange={(v) => onChange({ [f.field]: v } as Partial<PackageTypeStep1Values>)}
        />
      ))}
      <NumericLimitField
        label="MySQL databases"
        name="databases"
        value={values.databases}
        options={serverType === 'windows' ? WINDOWS_DB_OPTIONS : resoldTypeProto.databasen}
        allowCustom={serverType !== 'windows'}
        onChange={(v) => onChange({ databases: v })}
      />

      <div className="hi-ui-hosting-package-type-form__email-field">
        <FormField label="Welcome email" labelPosition="left">
          <Select
            name="welcome_email"
            value={String(values.welcomeEmail)}
            onChange={(e) =>
              onChange({ welcomeEmail: e.target.value === '' ? '' : Number(e.target.value) })
            }
            fullWidth
            disabled={emails.length === 0}
          >
            {emails.length === 0 ? (
              <option value="">Not configured</option>
            ) : (
              emails.map((em) => (
                <option key={em.id} value={em.id}>
                  {em.title}
                </option>
              ))
            )}
          </Select>
        </FormField>
        <div className="hi-ui-hosting-package-type-form__email-field-configure">
          <Link href={WE_CONFIGURE_URL} target="_blank" rel="noopener">
            Configure welcome emails
          </Link>
        </div>
      </div>

      <div className="hi-ui-hosting-package-type-form__email-field">
        <FormField label="Password reset email" labelPosition="left">
          <Select
            name="extend_password_reset_email"
            value={String(values.extendPasswordResetEmail)}
            onChange={(e) =>
              onChange({
                extendPasswordResetEmail: e.target.value === '' ? '' : Number(e.target.value),
              })
            }
            fullWidth
            disabled={eprEmails.length === 0}
          >
            {eprEmails.length === 0 ? (
              <option value="">Not configured</option>
            ) : (
              eprEmails.map((em) => (
                <option key={em.id} value={em.id}>
                  {em.title}
                </option>
              ))
            )}
          </Select>
        </FormField>
        <div className="hi-ui-hosting-package-type-form__email-field-configure">
          <Link href={EPRE_CONFIGURE_URL} target="_blank" rel="noopener">
            Configure password reset emails
          </Link>
        </div>
      </div>

      <Checkbox
        name={featuresFieldName}
        value="supportdb"
        checked={values.features.has('supportdb')}
        onChange={(e) => onToggleFeature('supportdb', e.target.checked)}
        label="Enable Support Database"
      />

      <Checkbox
        name="extend_domaincp_switcher"
        value="enabled"
        checked={values.extendDomaincpSwitcher}
        onChange={(e) => onChange({ extendDomaincpSwitcher: e.target.checked })}
        label="Enable eXtend to DomainCP switcher"
      />

      {snapshotsEnabled && (
        <>
          <Checkbox
            name={featuresFieldName}
            value="snapshot_backups"
            checked={values.features.has('snapshot_backups')}
            onChange={(e) => onToggleFeature('snapshot_backups', e.target.checked)}
            label="Enable Snapshot Backups"
            disabled={!hasSnapshots}
          />
          {!hasSnapshots && (
            <Text variant="body-small">
              You must purchase a{' '}
              <Link href={SNAPSHOT_BACKUPS_URL} target="_blank" rel="noopener">
                Snapshot Backups
              </Link>{' '}
              subscription to offer this service to your customers.
            </Text>
          )}
        </>
      )}
    </div>
  );
};
